/**
 * Token maker: pick an image, pan and zoom it inside a circle, choose a ring color, and get back
 * a small round token as a data URL (WebP when the browser can encode it, otherwise PNG).
 */

const OUT = 160;
const VIEW = 280;
const RINGS = ['#e9dfcf', '#ffb347', '#9fc5ff', '#8fd18b', '#ff6b6b', '#d7a8ff', '#1b1712'];

export interface TokenizerOptions {
  name: string;
  hasToken: boolean;
  onSave(image: string | null): void;
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export function openTokenizer(opts: TokenizerOptions): void {
  document.getElementById('token-modal')?.remove();
  const root = document.createElement('div');
  root.id = 'token-modal';
  root.innerHTML = `
    <div class="token-box" role="dialog" aria-label="Token for ${esc(opts.name)}">
      <h1>Token: ${esc(opts.name)}</h1>
      <p class="muted">Choose an image, then drag to position it and scroll (or use the slider) to zoom.</p>
      <div class="token-body">
        <div class="token-stage">
          <canvas width="${VIEW}" height="${VIEW}"></canvas>
          <div class="token-empty">Drop an image here<br>or click to choose one</div>
        </div>
        <div class="token-side">
          <label class="field">Zoom <input type="range" class="token-zoom" min="0.2" max="4" step="0.01" value="1"></label>
          <div class="field">Ring <div class="token-rings">${RINGS.map((c, i) => `<button type="button" class="ring ${i === 0 ? 'selected' : ''}" data-ring="${c}" style="background:${c}" title="${c}"></button>`).join('')}</div></div>
          <div class="token-preview-wrap"><canvas class="token-preview" width="${OUT}" height="${OUT}"></canvas><span class="muted">Preview</span></div>
        </div>
      </div>
      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="token-file" hidden>
      <div class="modal-actions">
        <button type="button" class="token-choose">Choose Image</button>
        ${opts.hasToken ? '<button type="button" class="token-remove">Remove Token</button>' : ''}
        <span class="spacer"></span>
        <button type="button" class="token-cancel">Cancel</button>
        <button type="button" class="primary token-save" disabled>Save Token</button>
      </div>
    </div>`;
  document.body.appendChild(root);

  const stage = root.querySelector<HTMLCanvasElement>('.token-stage canvas')!;
  const preview = root.querySelector<HTMLCanvasElement>('.token-preview')!;
  const file = root.querySelector<HTMLInputElement>('.token-file')!;
  const zoom = root.querySelector<HTMLInputElement>('.token-zoom')!;
  const save = root.querySelector<HTMLButtonElement>('.token-save')!;
  const empty = root.querySelector<HTMLElement>('.token-empty')!;
  let img: HTMLImageElement | null = null;
  let scale = 1, ox = 0, oy = 0;
  let ring = RINGS[0];

  const close = () => {
    root.remove();
    window.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  window.addEventListener('keydown', onKey, true);

  /** Draws the token at `size` pixels (k = size / VIEW); the guide shows the cropped-away image faintly. */
  const paint = (g: CanvasRenderingContext2D, size: number, k: number, withGuide: boolean) => {
    g.clearRect(0, 0, size, size);
    const r = size / 2 - 3 * k;
    if (withGuide) {
      g.fillStyle = '#0b0a09';
      g.fillRect(0, 0, size, size);
    }
    if (img) {
      const w = img.naturalWidth * scale * k, h = img.naturalHeight * scale * k;
      const x = size / 2 - w / 2 + ox * k, y = size / 2 - h / 2 + oy * k;
      if (withGuide) {
        g.globalAlpha = 0.35;
        g.drawImage(img, x, y, w, h);
        g.globalAlpha = 1;
      }
      g.save();
      g.beginPath();
      g.arc(size / 2, size / 2, r, 0, Math.PI * 2);
      g.clip();
      g.drawImage(img, x, y, w, h);
      g.restore();
    }
    g.lineWidth = 6 * k;
    g.strokeStyle = ring;
    g.beginPath();
    g.arc(size / 2, size / 2, r, 0, Math.PI * 2);
    g.stroke();
  };
  const redraw = () => {
    paint(stage.getContext('2d')!, VIEW, 1, true);
    paint(preview.getContext('2d')!, OUT, OUT / VIEW, false);
  };

  const load = (f: File | undefined) => {
    if (!f || !f.type.startsWith('image/')) return;
    const url = URL.createObjectURL(f);
    const next = new Image();
    next.onload = () => {
      img = next;
      scale = (VIEW - 6) / Math.min(next.naturalWidth, next.naturalHeight);
      zoom.min = String(scale * 0.5);
      zoom.max = String(scale * 4);
      zoom.value = String(scale);
      ox = oy = 0;
      empty.hidden = true;
      save.disabled = false;
      redraw();
      URL.revokeObjectURL(url);
    };
    next.src = url;
  };

  root.querySelector('.token-choose')!.addEventListener('click', () => file.click());
  empty.addEventListener('click', () => file.click());
  file.addEventListener('change', () => load(file.files?.[0]));
  stage.parentElement!.addEventListener('dragover', (e) => e.preventDefault());
  stage.parentElement!.addEventListener('drop', (e) => {
    e.preventDefault();
    load(e.dataTransfer?.files[0]);
  });
  zoom.addEventListener('input', () => {
    scale = Number(zoom.value);
    redraw();
  });
  stage.addEventListener('wheel', (e) => {
    if (!img) return;
    e.preventDefault();
    scale = Math.max(Number(zoom.min), Math.min(Number(zoom.max), scale * (e.deltaY < 0 ? 1.06 : 1 / 1.06)));
    zoom.value = String(scale);
    redraw();
  }, { passive: false });
  stage.addEventListener('pointerdown', (e) => {
    if (!img) return;
    stage.setPointerCapture(e.pointerId);
    const sx = e.clientX - ox, sy = e.clientY - oy;
    const move = (ev: PointerEvent) => {
      ox = ev.clientX - sx;
      oy = ev.clientY - sy;
      redraw();
    };
    const up = () => {
      stage.removeEventListener('pointermove', move);
      stage.removeEventListener('pointerup', up);
    };
    stage.addEventListener('pointermove', move);
    stage.addEventListener('pointerup', up);
  });
  root.querySelector('.token-rings')!.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-ring]');
    if (!b) return;
    ring = b.dataset.ring!;
    root.querySelectorAll('.ring').forEach((x) => x.classList.toggle('selected', x === b));
    redraw();
  });
  root.querySelector('.token-cancel')!.addEventListener('click', close);
  root.querySelector('.token-remove')?.addEventListener('click', () => {
    opts.onSave(null);
    close();
  });
  root.addEventListener('pointerdown', (e) => {
    if (e.target === root) close();
  });
  save.addEventListener('click', () => {
    if (!img) return;
    paint(preview.getContext('2d')!, OUT, OUT / VIEW, false);
    let data = preview.toDataURL('image/webp', 0.9);
    if (!data.startsWith('data:image/webp')) data = preview.toDataURL('image/png');
    opts.onSave(data);
    close();
  });
  redraw();
}

// ------------------------------------------------------------ token images for the map

const images = new Map<string, HTMLImageElement>();

export function tokenUrl(key: string): string {
  return `/tokens/${encodeURIComponent(key)}`;
}

/** A loaded token image, or null while it's still loading (callers draw a fallback meanwhile). */
export function tokenImage(key: string | undefined): HTMLImageElement | null {
  if (!key) return null;
  let img = images.get(key);
  if (!img) {
    img = new Image();
    img.src = tokenUrl(key);
    images.set(key, img);
  }
  return img.complete && img.naturalWidth > 0 ? img : null;
}
